import { ResolvableLiteral } from '@uirouter/angular';

import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';
import { WebcastModelService } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel.Service';
import { WebcastService } from 'rev-portal/scheduledEvents/webcast/Webcast.Service';
import { WebcastSidebarNotificationsService } from 'rev-portal/scheduledEvents/webcast/WebcastSidebarNotificationsService';

import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { SessionService } from 'rev-shared/security/Session.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { AnalyticsViewContext } from 'rev-shared/analytics/Constant';

export const resolveBootstrapWebcastId: ResolvableLiteral = {
	token: 'webcastId',
	resolveFn: () => BootstrapContext.webcastId
};
const useTokenAuth = BootstrapContext.tokenParam != null;

export const resolveWebcast = (onLoad?: (w: WebcastModel) => void, dependency?: string) => {
	return {
		token: 'webcast',
		deps: ['webcastId', WebcastService, dependency].filter(Boolean),
		resolveFn: (webcastId, WebcastService: WebcastService) => {
			if (!webcastId) {
				return Promise.reject({ status: 401 });
			}
			return WebcastService.getWebcast(webcastId, true, false)
				.then(w => {
					onLoad?.(w);
					return w;
				})
				.catch(e => {
					console.error(e);
					if(e.status === 401) {
						const noLoginRedirect = useTokenAuth;
						return Promise.reject({ status: 401, reason: 'event',
							data: { noLoginRedirect }
						});
					}
					throw new Error('Error loading webcast');
				});
		}
	};
};

export const resolveWebcastConnection: ResolvableLiteral = {
	token: 'webcastConnection',
	deps: [WebcastModelService, 'webcast', 'isPreProd', 'analyticsViewContext'],
	resolveFn: (WebcastModelService: WebcastModelService, webcast: WebcastModel, isPreProd: boolean, analyticsViewContext = AnalyticsViewContext.Unknown) => {
		if(isPreProd ?
			!webcast.currentUser.canJoinPreProduction :
			!webcast.currentUser.allowJoin) {
			console.error('Cannot join webcast');
			return Promise.reject({ status: 401 });
		}

		webcast.assignCurrentRun(isPreProd);
		webcast.analyticsViewContext = analyticsViewContext;
		return WebcastModelService.connect(webcast)
			.catch(err => {
				console.error('webcast connection error', err);
				if(err.hasIssue) {
					if (err.hasIssue('AttendeeRemoved')) {
						return Promise.reject({ status:401, data: { reason:'AttendeeRemoved', noLoginRedirect: true } });
					}
				}

				return Promise.reject(err);
			});
	}
};

export const resolveUserSubscription = {
	token: 'userSubscription',
	deps: [PushBus, UserContextService, 'webcast'],
	resolveFn: (PushBus: PushBus, UserContext: UserContextService, webcast: WebcastModel) => {
		return PushBus.subscribe(UserContext.getUser().id, {
			WebcastAttendeeRemoved() {
				webcast.currentUser.isRemovedFromWebcast = true;
			},
			WebcastAttendeeRestored() {
				webcast.currentUser.isRemovedFromWebcast = false;
			}
		});
	}
};

export const resolveSessionKeepalive = {
	token: 'sessionKeepAlive',
	deps: [SessionService],
	resolveFn: (Session: SessionService) => Session.createKeepalive().beginWhenConnected()
};

export const resolveSidebarNotifications = {
	token: 'sidebarNotifications',
	deps: [WebcastSidebarNotificationsService, 'webcast', 'onlyWebcastEngagementUsage'],
	resolveFn: (WebcastSidebarNotificationsService: WebcastSidebarNotificationsService, webcast: WebcastModel, onlyWebcastEngagementUsage) => {
		return WebcastSidebarNotificationsService.getSidebarNotifications(webcast, onlyWebcastEngagementUsage);
	}
};

export const resolveWebcastPushUnsubscribe = {
	token: 'webcastPushHandlersUnsubscribe',
	deps: [WebcastModelService, 'webcast'],
	resolveFn: (WebcastModelService: WebcastModelService, webcast: WebcastModel) => {
		return webcast && WebcastModelService.registerWebcastPushHandlers(webcast);
	}
};

export const resolvePollsSubscription = {
	token: 'pollsPushSubscription',
	deps: ['webcast'],
	resolveFn: (webcast: WebcastModel) => webcast.polls.subscribePush().subscribe()
};

export const mediaFeaturesResolves = [
	{
		token: 'mediaFeatures',
		deps: [MediaFeaturesService],
		resolveFn: (MediaFeatures: MediaFeaturesService) => MediaFeatures.getFeatures()
	},
	{
		token: 'initFeatureSettings',
		deps: ['mediaFeatures', 'webcast'],
		resolveFn: (mediaFeatures: IMediaFeatures, webcast: WebcastModel) => {
			if (webcast) {
				webcast.initFeatures(mediaFeatures);
			}
		}
	},
];
